import { useState } from "react";

interface PasswordValidationResult {
  isValid: boolean;
  error: string;
}

interface PasswordMatchResult {
  isValid: boolean;
  error: string;
}

export const usePasswordValidation = () => {
  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

  const validatePassword = (password: string): PasswordValidationResult => {
    // Verificar si está vacío
    if (!password.trim()) {
      return {
        isValid: false,
        error: "La contraseña es obligatoria",
      };
    }

    // Verificar longitud mínima
    if (password.length < 8) {
      return {
        isValid: false,
        error: "La contraseña debe tener al menos 8 caracteres",
      };
    }

    // Verificar que no contenga espacios
    if (password.includes(" ")) {
      return {
        isValid: false,
        error: "La contraseña no puede contener espacios",
      };
    }

    // Verificar mayúscula
    if (!/[A-Z]/.test(password)) {
      return {
        isValid: false,
        error: "La contraseña debe incluir al menos una letra mayúscula",
      };
    }

    // Verificar minúscula
    if (!/[a-z]/.test(password)) {
      return {
        isValid: false,
        error: "La contraseña debe incluir al menos una letra minúscula",
      };
    }

    // Verificar número
    if (!/[0-9]/.test(password)) {
      return {
        isValid: false,
        error: "La contraseña debe incluir al menos un número",
      };
    }

    // Verificar carácter especial
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return {
        isValid: false,
        error:
          "La contraseña debe incluir al menos un carácter especial (!@#$%^&*()_+-=[]{}|;:,.<>?)",
      };
    }

    return {
      isValid: true,
      error: "",
    };
  };

  const validatePasswordMatch = (
    password: string,
    confirmPassword: string
  ): PasswordMatchResult => {
    if (!confirmPassword.trim()) {
      return {
        isValid: false,
        error: "Debe confirmar la contraseña",
      };
    }

    if (password !== confirmPassword) {
      return {
        isValid: false,
        error: "Las contraseñas no coinciden",
      };
    }

    return {
      isValid: true,
      error: "",
    };
  };

  const handlePasswordChange = (password: string) => {
    const validation = validatePassword(password);
    setPasswordError(validation.error);
    return validation.isValid;
  };

  const handleConfirmPasswordChange = (
    password: string,
    confirmPassword: string
  ) => {
    const validation = validatePasswordMatch(password, confirmPassword);
    setConfirmPasswordError(validation.error);
    return validation.isValid;
  };

  const clearPasswordError = () => {
    setPasswordError("");
  };

  const clearConfirmPasswordError = () => {
    setConfirmPasswordError("");
  };

  return {
    passwordError,
    confirmPasswordError,
    validatePassword,
    validatePasswordMatch,
    handlePasswordChange,
    handleConfirmPasswordChange,
    clearPasswordError,
    clearConfirmPasswordError,
  };
};
